import { Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { RadioButtonGroupInput, TextInput, useNotify } from "react-admin";
import axios from "axios";

const OrderEditConfirmModal = ({
  chooseComment,
  change,
  fulfillmentServiceFunc,
  trackingUrlFunc,
  trackingIdFunc,
  posBillNumberFunc,
  warehouse,
  setIsUrlValid,
}) => {
  const [urlError, setUrlError] = useState("");
  const [warehouseData, setWarehouseData] = useState([]);
  const [isLocalWarehouse, setIsLocalWarehouse] = useState(false); // Keep default as false
  const [isLoadingWarehouseData, setIsLoadingWarehouseData] = useState(true); // Add loading state
  const notify = useNotify();
  
  // Fetch warehouse data from API
  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const { accessToken } = JSON.parse(localStorage.getItem("auth")) || { accessToken: null };
        if (!accessToken) return;
        
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/wms/api/warehouses`,
          {
            headers: {
              authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
              accept: "application/json",
            },
          }
        );
        
        if (response.data.success) {
          setWarehouseData(response.data.data);
          
          // Check if current warehouse is local
          const currentWarehouse = response.data.data.find(wh => wh.name === warehouse);
          let isLocal = false; // Default to false if warehouse not found
          if (currentWarehouse) {
            // Explicitly check for true boolean or case-insensitive 'true' string
            isLocal = currentWarehouse.is_local === true || String(currentWarehouse.is_local).toLowerCase() === 'true';
          }
          setIsLocalWarehouse(isLocal); // Set state with corrected boolean value
        }
      } catch (error) {
        console.error("Error fetching warehouse data:", error);
        // Ensure loading state is set even on error
      } finally {
        setIsLoadingWarehouseData(false); // Set loading to false after fetch attempt
      }
    };
    
    fetchWarehouseData();
  }, [warehouse]);
  
  // Show tracking URL and ID fields when warehouse is not local and status is dispatched
  const showTrackingFields = !isLocalWarehouse && change === "dispatched";
  
  // Show shipping method when not a local warehouse and status is dispatch
  const showShippingMethod = change === "dispatch" && !isLocalWarehouse;
  
  useEffect(() => {
    if (showTrackingFields) {
      setIsUrlValid(false);
    } else {
      setIsUrlValid(true);
    }
  }, [showTrackingFields, setIsUrlValid]);

  const validateUrl = (value) => {
    if (!value) {
      setUrlError('Tracking URL is required');
      setIsUrlValid(false);
      return 'Tracking URL is required';
    }

    // Remove leading/trailing whitespace
    value = value.trim();
    
    // Check for whitespace within the URL
    if (value.includes(' ')) {
      setUrlError('URL cannot contain spaces');
      setIsUrlValid(false);
      return 'URL cannot contain spaces';
    }

    // URL pattern that requires domain extension and proper format
    const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
    
    // Add https:// if not present for URL constructor
    const urlToTest = value.startsWith('http') ? value : `https://${value}`;

    try {
      // First check if it matches basic URL pattern
      if (!urlPattern.test(value)) {
        throw new Error('Invalid URL format');
      }

      // Then try to construct URL object for additional validation
      new URL(urlToTest);

      // Make sure it has a valid domain extension
      const domainParts = value.replace(/^https?:\/\//, '').split('.');
      if (domainParts.length < 2 || domainParts[domainParts.length - 1].length < 2) {
        throw new Error('Invalid domain');
      }

      setUrlError("");
      setIsUrlValid(true);
    } catch (error) {
      const errorMsg = 'Please enter a valid URL (e.g., example.com or https://example.com)';
      setUrlError(errorMsg);
      setIsUrlValid(false);
      return errorMsg;
    }
  };

  const handleUrlChange = (e) => {
    const value = e.target.value;
    const validationError = validateUrl(value);
    
    if (!validationError) {
      trackingUrlFunc(value);
    } else {
      notify(validationError, { type: 'error' });
      trackingUrlFunc(""); // Clear the tracking URL if invalid
    }
  };

  return (
    <div>
      <Typography component="legend" fontWeight="400" fontSize="18px">
        Are you sure you want to update the status of the order?
      </Typography>
      <TextInput
        source="comment"
        multiline
        onChange={(e) => chooseComment(e.target.value)}
        label="Add Comment"
        fullWidth
      />
      {/* Only render conditional fields after warehouse data is loaded */}
      {!isLoadingWarehouseData && (
        <>
          {showShippingMethod && (
            <RadioButtonGroupInput
              source="carrier"
              label="Fulfillment Service"
              onChange={(e) => fulfillmentServiceFunc(e.target.value)}
              defaultValue='dtdc'
              choices={[
                { id: "dtdc", name: "DTDC" },
                { id: "shiprocket", name: "Shiprocket" },
                { id: "manual", name: "Manual" },
                { id: "delhivery", name: "Delhivery" },
              ]}
            />
          )}
          {showTrackingFields && (
            <>
              <TextInput
                source="customer.trackingId"
                onChange={(e) => trackingIdFunc(e.target.value)}
                label="Tracking ID"
                fullWidth
                required
              />
              <TextInput
                source="customer.trackingUrl"
                onChange={handleUrlChange}
                label="Tracking URL"
                fullWidth
                required
                error={!!urlError}
                helperText={urlError || "Enter a valid URL (e.g., tracking.example.com or https://tracking.example.com)"}
              />
            </>
          )}
          {change === "billed" && (
            <TextInput
              required
              source="customer.posBillNumber"
              onChange={(e) => posBillNumberFunc(e.target.value)}
              label="POS Bill Number"
              fullWidth
            />
          )}
        </>
      )}
    </div>
  );
};

export default OrderEditConfirmModal;